import { historicoForPeople, liberacoesForPeople } from '../../utils/liberacoes';
import { UnitLiberado, UnitLiberadoHistory, UnitLiberadoKind } from './UnitLiberadosSection';

/**
 * The unit drawer's clearance lists — live and ended — from the people payload.
 *
 * Both go through utils/liberacoes, the function the Operacional frame and the gate banner also
 * use. The drawer used to hand-roll its own "is this a liberação?" test and its own merge of two
 * separately-filtered arrays, which is how it came to say "Nenhum visitante liberado no momento"
 * for a unit whose Operacional frame said "LIBERADOS 6" at the same moment.
 *
 * See unitLiberados.test.ts.
 */

/**
 * What this unit's clearances USED to be.
 *
 * Takes the people payload straight from `?liberado=history` and narrows it to ONE unit: the
 * payload carries every clearance each person holds — somebody may have been cleared for three
 * units — and a unit page listing the others would be showing another household's visitors.
 *
 * Without a unit to scope by it shows nothing rather than everything, for the same reason.
 */
export function toUnitHistorico(
  people: any[] | null | undefined,
  unitId: string | undefined,
  now: Date = new Date()
): UnitLiberadoHistory[] {
  if (!unitId || !Array.isArray(people)) return [];

  return historicoForPeople(people, now)
    .rows.filter((row) => row.unitId === unitId)
    .map((row) => ({
      personId: row.personId,
      roleId: row.roleId,
      name: row.personName,
      photo: row.person?.identity_photo_url || row.person?.profile_picture_url || '',
      kind: row.kind === 'grade' ? 'grade' : row.kind === 'always' ? 'always' : 'period',
      endedLabel: row.endedLabel,
      endedAt: row.endedAt,
      isServiceProvider: row.isServiceProvider,
    }));
}

/**
 * The unit's LIVE clearances, from the SAME rule every other surface uses.
 *
 * `liberacoesForPeople` honours the server's computed status when it is there and the full local
 * predicate when it is not — including the standing authorisation ("válido até <mais de 24h>")
 * that the portaria's own form creates and that the drawer's old predicate ignored.
 *
 * Ordered sempre-habilitado first: it is the strongest clearance in the product and the one an
 * operator most needs to notice when scanning a unit.
 */
export function toUnitLiberadosFromPeople(
  people: any[] | null | undefined,
  unitId: string | undefined,
  now: Date = new Date()
): UnitLiberado[] {
  if (!unitId || !Array.isArray(people)) return [];

  const rows = liberacoesForPeople(people, now)
    .rows
    // A role with no unit recorded belongs to whichever unit is asking — the fallback the page
    // has always had, for liberações written before the unit was stamped.
    .filter((row) => !row.unitId || row.unitId === unitId)
    .map((row) => ({
      personId: row.personId,
      roleId: row.roleId,
      name: row.personName,
      photo: row.person?.identity_photo_url || row.person?.profile_picture_url || '',
      kind: (row.kind === 'always' ? 'always' : row.kind === 'grade' ? 'grade' : 'period') as UnitLiberadoKind,
      isServiceProvider: row.isServiceProvider,
      // The sentence the shared summary already composed ("Período liberado · 16/10/2026 até
      // 17/10/2026"), so the drawer says exactly what the Operacional frame says about the same
      // clearance. A second rendering of the same facts is how the two drifted apart.
      description: [row.periodLabel, row.rangeLabel].filter(Boolean).join(' · '),
    }));

  return [
    ...rows.filter((r) => r.kind === 'always'),
    ...rows.filter((r) => r.kind !== 'always'),
  ];
}
